import React from "react";
import VisionArt from "../../../../assets/images/vision-art.png";
import styles from "./VisionSection.scss";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

// 平面的なSVGアイコン（#E395B7色）
const EncounterIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="8" stroke="#E395B7" strokeWidth="3" fill="none" />
    <path d="M12 42c0-8 6-12 8-12s8 4 8 12" stroke="#E395B7" strokeWidth="3" strokeLinecap="round" fill="none" />
    <circle cx="44" cy="20" r="8" stroke="#E395B7" strokeWidth="3" fill="none" />
    <path d="M36 42c0-8 6-12 8-12s8 4 8 12" stroke="#E395B7" strokeWidth="3" strokeLinecap="round" fill="none" />
    <path d="M28 36h8" stroke="#E395B7" strokeWidth="3" strokeLinecap="round" />
    <path d="M32 32v8" stroke="#E395B7" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const EmpathyIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M32 56l-2.8-2.5C15.6 41.3 8 34.5 8 26c0-7 5.5-12 12-12 3.6 0 7.1 1.7 9.4 4.3L32 21l2.6-2.7C37 15.7 40.4 14 44 14c6.5 0 12 5 12 12 0 8.5-7.6 15.3-21.2 27.5L32 56z"
      stroke="#E395B7"
      strokeWidth="3"
      fill="none"
    />
    <circle cx="24" cy="30" r="2" fill="#E395B7" />
    <circle cx="40" cy="30" r="2" fill="#E395B7" />
    <path d="M26 38c2 2 8 2 12 0" stroke="#E395B7" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

const InspirationIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M32 8c-10 0-16 7-16 16 0 6 3 10 6 14 2 3 4 5 4 8v4h12v-4c0-3 2-5 4-8 3-4 6-8 6-14 0-9-6-16-16-16z"
      stroke="#E395B7"
      strokeWidth="3"
      fill="none"
    />
    <path d="M24 54h16" stroke="#E395B7" strokeWidth="3" strokeLinecap="round" />
    <path d="M26 58h12" stroke="#E395B7" strokeWidth="3" strokeLinecap="round" />
    <path d="M32 2v4" stroke="#E395B7" strokeWidth="2" strokeLinecap="round" />
    <path d="M48 10l-3 3" stroke="#E395B7" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 10l3 3" stroke="#E395B7" strokeWidth="2" strokeLinecap="round" />
    <path d="M54 24h-4" stroke="#E395B7" strokeWidth="2" strokeLinecap="round" />
    <path d="M14 24h-4" stroke="#E395B7" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export function VisionSection() {
  // スクロールアニメーション
  const [holeRef, holeVisible] = useScrollAnimation({ threshold: 0.3 });
  const [cardsRef, cardsVisible] = useScrollAnimation({ threshold: 0.2 });

  const visions = [
    {
      id: "encounter",
      title: "Serendipity | 出会い",
      description:
        "自分とは異なる世界や考え方と出会い、\n「当たり前」の範囲が広がっていく。\n思いがけない対話が、次の学びや創造のきっかけになる。",
      icon: <EncounterIcon />
    },
    {
      id: "empathy",
      title: "Sympathy | 共感",
      description:
        "想いを共有し、励まし合い、\n共に考える仲間ができる。\n共感を軸にしたつながりが、新しい挑戦を支えていく。",
      icon: <EmpathyIcon />
    },
    {
      id: "inspiration",
      title: "Inspire | 触発",
      description:
        "誰かの情熱や行動に刺激を受けて、\n自分も一歩踏み出したくなる。\nそこからまた、新しいコミュニティやプロジェクトが生まれていく。",
      icon: <InspirationIcon />
    }
  ];

  return (
    <section id="vision" className={styles.visionSection}>
      {/* アート画像 - セクション右上に配置 */}
      <img
        src={VisionArt}
        alt=""
        className={`${styles.holeArt} ${holeVisible ? styles.artAnimate : styles.artHidden}`}
      />
      {/* 不定形の緑のくぼみ穴 */}
      <div
        ref={holeRef}
        className={`${styles.holeContainer} ${holeVisible ? styles.holeAnimate : styles.holeHidden}`}
      >
        {/* アニメーションする穴 */}
        <svg className={styles.holeSvg} viewBox="-10 -10 120 120" preserveAspectRatio="none">
          <defs>
            <linearGradient id="visionHoleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#C4E4D2" />
              <stop offset="50%" stopColor="#BEDBE4" />
              <stop offset="100%" stopColor="#C4E4D2" />
            </linearGradient>
            <filter id="visionInnerShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feFlood floodColor="black" floodOpacity="1" result="flood" />
              <feComposite in="flood" in2="SourceAlpha" operator="out" result="outside" />
              <feOffset in="outside" dx="3" dy="3" result="offsetShadow" />
              <feGaussianBlur in="offsetShadow" stdDeviation="4" result="blurShadow" />
              <feComposite in="blurShadow" in2="SourceAlpha" operator="in" result="innerShadow" />
              <feColorMatrix
                in="innerShadow"
                type="matrix"
                result="coloredShadow"
                values="0 0 0 0 0
                        0 0 0 0 0
                        0 0 0 0 0
                        0 0 0 0.5 0"
              />
              <feMerge>
                <feMergeNode in="SourceGraphic" />
                <feMergeNode in="coloredShadow" />
              </feMerge>
            </filter>
          </defs>
          <path fill="url(#visionHoleGradient)" filter="url(#visionInnerShadow)">
            <animate
              attributeName="d"
              dur="20s"
              repeatCount="indefinite"
              values="
                M 25 8 C 35 -5 55 -2 68 8 S 92 0 102 22 S 115 55 105 75 S 112 100 80 105 S 45 120 5 125 S -30 115 -25 85 S -15 45 25 8 Z;
                M 28 12 C 38 0 52 2 65 10 S 90 5 100 25 S 112 58 102 78 S 108 102 78 108 S 42 122 2 128 S -28 118 -22 88 S -12 48 28 12 Z;
                M 22 5 C 32 -8 58 -5 70 5 S 95 -2 105 20 S 118 52 108 72 S 115 98 82 102 S 48 118 8 122 S -32 112 -28 82 S -18 42 22 5 Z;
                M 25 8 C 35 -5 55 -2 68 8 S 92 0 102 22 S 115 55 105 75 S 112 100 80 105 S 45 120 5 125 S -30 115 -25 85 S -15 45 25 8 Z
              "
            />
          </path>
        </svg>
      </div>
      <div className={styles.visionContainer}>
        <h2 className={styles.visionTitle}>Vision</h2>
        <p className={styles.visionSubtitle}>想いが出会い、共感し、触発し合う。そこから新しい未来が動き出す。</p>
        <div ref={cardsRef} className={styles.visionCards}>
          {visions.map((vision, index) => (
            <div
              key={vision.id}
              className={`${styles.visionCard} ${cardsVisible ? styles.cardAnimate : styles.cardHidden}`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className={styles.cardIcon}>{vision.icon}</div>
              <div className={styles.cardTextArea}>
                <h3 className={styles.cardTitle}>{vision.title}</h3>
                <p className={styles.cardDescription}>{vision.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.visionFooter}>
          <p>MetaCampUsは、「出会い・共感・触発」の循環から生まれる、新しい学びと創造のエコシステムを育てています。</p>
          <p>ここから生まれたコミュニティや活動が、また次の場を育てていく。</p>
          <p>MetaCampUsは、そんな"コミュニティのためのプラットフォーム"として、未来の共想と共創を支えていきます。</p>
        </div>
      </div>
    </section>
  );
}
